import { cn } from "../../utils/common/cn";
import { VALUE_BASIS_LABEL, type ValueBasis } from "../../utils/portfolio-risk-concentration/api/concentration";
import { usePortfolioTotal } from "../../utils/portfolio-risk-concentration/api/portfolio-total";
import { FilterField } from "./PrcFilterBar";

/** "$1,835.0M": dollars in millions to one decimal, with thousands separators (the full dollars are in the card's tooltip). */
export function totalMillions(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${v < 0 ? "-" : ""}$${(Math.abs(v) / 1_000_000).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
}

export function totalCount(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : v.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

/** One small figure in the filter row's style: the label above, the figure in a bordered box the height of a dropdown. `sub` is a muted
 *  second figure beside it (Scenario Monitor's "now"). */
function TotalCard({ label, text, sub, title, className }: { label: string; text: string; sub?: string; title?: string; className?: string }) {
  return (
    <FilterField label={label}>
      <div
        title={title}
        className={cn(
          "flex h-9 min-w-[128px] items-center gap-2 whitespace-nowrap border border-border bg-card px-3 text-sm font-semibold tabular-nums text-foreground",
          className,
        )}
      >
        {text}
        {sub ? <span className="text-[11px] font-normal text-muted-foreground">{sub}</span> : null}
      </div>
    </FilterField>
  );
}

/**
 * The two cards of a portfolio selection, Total value and Contract count, for the right of a filter row. Presentational: Monitor feeds it
 * from /portfolio-total (MonitorTotalCards below) and Scenario Monitor from its own scenario answer, where `currentValue` / `currentCount`
 * are the book as it is today and `value` / `count` what it becomes under the scenario.
 */
export function TotalCards({
  value,
  count,
  currentValue,
  currentCount,
  basisLabel,
  dimmed,
  note,
}: {
  value: number | null | undefined;
  count: number | null | undefined;
  currentValue?: number | null;
  currentCount?: number | null;
  basisLabel?: string;
  /** the figures are the previous selection's while the new ones load */
  dimmed?: boolean;
  /** shown as the cards' tooltip, e.g. why an investor's total is 0 */
  note?: string | null;
}) {
  const compare = currentValue !== undefined || currentCount !== undefined;
  return (
    <div className={cn("flex items-end gap-3", dimmed && "opacity-50")}>
      <TotalCard
        label="Total value"
        text={totalMillions(value)}
        sub={compare ? `now ${totalMillions(currentValue)}` : undefined}
        title={[
          value === null || value === undefined ? undefined : `$${Math.round(value).toLocaleString("en-US")}`,
          basisLabel ? `Measured in ${basisLabel}` : undefined,
          note ?? undefined,
        ]
          .filter(Boolean)
          .join(" · ")}
      />
      <TotalCard
        label="Contract count"
        text={totalCount(count)}
        sub={compare ? `now ${totalCount(currentCount)}` : undefined}
        title={note ?? undefined}
      />
    </div>
  );
}

/** Monitor's cards: the total of the book the investor, facility and Loans / Leases filters name, on the chosen basis, as of the page's date. */
export function MonitorTotalCards({
  investor,
  facility,
  product,
  valueBasis,
  asOfDate,
}: {
  investor: string;
  facility: string;
  product: string;
  valueBasis: ValueBasis;
  asOfDate: string;
}) {
  const q = usePortfolioTotal({ investor, facility, product, valueBasis, asOfDate });
  const d = q.data;
  return (
    <TotalCards
      value={q.isError ? null : d?.value}
      count={q.isError ? null : d?.contractCount}
      basisLabel={VALUE_BASIS_LABEL[valueBasis]}
      dimmed={q.isFetching && !!d}
      note={q.isError ? "Could not load the portfolio total." : d?.note}
    />
  );
}
